import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import "@/components/Users/UsersModal/UsersModal.css";

import UserModalServices from "@/components/Users/UsersModal/UserModalServices";
import UserDeleteForm from "@/components/Users/UsersModal/UsersModalComponents/UserDeleteForm";
import UserEditForm from "@/components/Users/UsersModal/UsersModalComponents/UserEditForm";
import UserInvitationForm from "@/components/Users/UsersModal/UsersModalComponents/UserInvitationForm";
import { canManageAllUsers } from "@/utils/permissionsHandler";
import { validateEmail } from "@/utils/util";

function UsersModal(props) {
  const {
    open,
    handleClose,
    modalState,
    dataToAlter,
    updateTable,
    managementToken,
    affiliates,
    setNotification,
  } = props;

  const [name, setName] = useState("");
  const [chosenAffiliate, setChosenAffiliate] = useState(undefined);
  const [chosenRole, setChosenRole] = useState(undefined);
  const [email, setEmail] = useState("");
  const [errorMessages, setErrorMessages] = useState([]);
  const [disableForm, setDisableForm] = useState(true);
  const { user } = useAuth0();

  useEffect(() => {
    if (modalState === "edit") {
      setEmail(dataToAlter.email);
      setName(dataToAlter.name);
    }
  }, [dataToAlter, modalState]);

  useEffect(() => {
    // FORM VALIDATION
    setErrorMessages([]);

    const areBothFieldsFilledOut = !!(name && email); // both fields need to be filled out
    const isValidEmail = validateEmail(email); // valid email
    const hasEitherFieldChanged =
      name !== dataToAlter.name || email !== dataToAlter.email; // has either field changed
    let didSystemAdminFailToChooseAffiliate = true;
    let didSystemAdminFailToChooseRole = true;
    if (modalState === "invite" && canManageAllUsers(user) && !chosenAffiliate)
      didSystemAdminFailToChooseAffiliate = false;
    if (modalState === "invite" && canManageAllUsers(user) && !chosenRole)
      didSystemAdminFailToChooseRole = false;

    setDisableForm(
      !(
        areBothFieldsFilledOut &&
        isValidEmail &&
        hasEitherFieldChanged &&
        didSystemAdminFailToChooseAffiliate &&
        didSystemAdminFailToChooseRole
      )
    );

    // ERROR MESSAGES
    const tempErrors = [];

    if (!name) {
      tempErrors.push("please enter a name.");
    }

    if (!email) {
      tempErrors.push("please enter an email.");
    }

    if (!isValidEmail && email) {
      tempErrors.push("invalid email");
    }

    if (modalState === "invite" && canManageAllUsers(user)) {
      if (!chosenRole) {
        tempErrors.push("please choose a role.");
      }
      if (!chosenAffiliate) {
        tempErrors.push("please choose an affiliate.");
      }
    }

    setErrorMessages(tempErrors);
  }, [name, email, chosenAffiliate, chosenRole, modalState, dataToAlter, user]);

  const onTextfieldChangeHandler = (e) => {
    const { name, value } = e.target;
    if (name === "email") {
      setEmail(value);
    } else if (name === "name") {
      setName(value);
    }
  };

  const cleanUpModal = () => {
    handleClose();
  };

  const { inviteUserHandler, deleteUserHandler, editUserHandler } =
    UserModalServices(
      dataToAlter,
      updateTable,
      cleanUpModal,
      name,
      email,
      managementToken,
      chosenAffiliate,
      chosenRole,
      user,
      setNotification
    );

  const userInvitationProps = {
    handleClose,
    onTextfieldChangeHandler,
    setChosenAffiliate,
    setChosenRole,
    disableForm,
    inviteUserHandler,
    user,
    affiliates,
    chosenRole,
    chosenAffiliate,
    errorMessages,
  };
  const userDeleteProps = {
    handleClose,
    dataToAlter,
    deleteUserHandler,
  };
  const userEditProps = {
    handleClose,
    dataToAlter,
    onTextfieldChangeHandler,
    disableForm,
    editUserHandler,
    errorMessages,
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="users-modal-inner" data-testid="users-modal">
        {modalState === "edit" && <UserEditForm {...userEditProps} />}

        {modalState === "invite" && (
          <UserInvitationForm {...userInvitationProps} />
        )}

        {modalState === "delete" && <UserDeleteForm {...userDeleteProps} />}
      </Box>
    </Modal>
  );
}
UsersModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  modalState: PropTypes.string,
  dataToAlter: PropTypes.any,
  updateTable: PropTypes.func,
  token: PropTypes.string,
  managementToken: PropTypes.string,
  affiliates: PropTypes.array,
};
export default UsersModal;
